import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Event, ProjectFile } from '@/types';
import { useApp } from '@/context/AppContext';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { useModalStack } from '@/context/ModalStackContext';
import VideoModal from './VideoModal';
import { isImageMedia, isVideoMedia, isPdfMedia } from '@/lib/media/mediaTypes';
import { downloadMedia } from '@/lib/media/downloadMedia';
import { canOpenPdfNatively, openPdfNative } from '@/lib/media/openPdfNative';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Download,
  FileText,
  Image as ImageIcon,
  Eye,
  CheckCircle2,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import ImageGallery from './ImageGallery';
import { EventService } from '@/services/EventService';

interface EventDetailsModalProps {
  event: Event | null;
  open: boolean;
  onClose: () => void;
  onEdit?: (event: Event) => void;
}

const eventTypeLabels = {
  visit: 'ביקור',
  progress: 'שאלה',
  fault: 'תקלה',
};

const eventTypeColors = {
  visit: 'bg-primary text-primary-foreground',
  progress: 'bg-success text-success-foreground',
  fault: 'bg-destructive text-destructive-foreground',
};

const eventTypeIcons = {
  visit: Eye,
  progress: CheckCircle2,
  fault: AlertCircle,
};

export default function EventDetailsModal({ event, open, onClose, onEdit }: EventDetailsModalProps) {
  const { getEventFiles } = useApp();
  const [previewFile, setPreviewFile] = useState<ProjectFile | null>(null);
  const [previewVideo, setPreviewVideo] = useState<ProjectFile | null>(null);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const modalId = useRef(`event-modal-${Date.now()}`).current;
  const { registerModal } = useModalStack();
  const [videoModalIsOpen, setVideoModalIsOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  // רישום החלון במחסנית החלונות הקופצים (Register modal in stack)
  useEffect(() => {
    if (!open || !event) return;
    return registerModal({
      id: modalId,
      priority: 1000,
      onClose,
    });
  }, [open, event, registerModal, modalId, onClose]);

  const handleGalleryClosed = useCallback(() => {
    setIsGalleryOpen(false);
  }, []);

  // שמירת מערך התמונות למניעת חישוב כפול (Memoizing image array)
  const attachedFiles = event ? (event.files || getEventFiles(event.id) || []) : [];
  const imageFiles = useMemo(() => attachedFiles.filter(f => isImageMedia({ type: f.type })), [attachedFiles]);

  if (!event) return null;

  const Icon = eventTypeIcons[event.type];

  const isPreviewable = (file: ProjectFile): boolean => {
    return isImageMedia({ type: file.type }) || isPdfMedia({ type: file.type });
  };

  const handleFileClick = async (file: ProjectFile) => {
    if (isImageMedia({ type: file.type })) {
      const index = imageFiles.findIndex(f => f.id === file.id);
      setGalleryIndex(index >= 0 ? index : 0);
      setIsGalleryOpen(true);
    } else if (isPdfMedia({ type: file.type })) {
      // Android: open in the device's native PDF viewer; fall back to the in-app modal on failure.
      if (canOpenPdfNatively(file.url)) {
        const res = await openPdfNative({ url: file.url, fileName: file.name });
        if (res.opened) return;
        if (res.reason === 'download-failed') {
          toast({ title: 'פתיחת הקובץ נכשלה', variant: 'destructive' });
        }
      }
      setPreviewFile(file);
    } else if (isPreviewable(file)) {
      setPreviewFile(file);
    } else if (isVideoMedia({ type: file.type })) {
      // type-only: matches backend 'VIDEO' and legacy raw-MIME 'video/*'; VideoModal just plays the url, no downstream type re-gate.
      setPreviewVideo(file);
      setVideoModalIsOpen(true);
    }else{
      triggerDownload(file);
    }
  };

  const resolveShareTitle = (ev: Event): string => {
    const fromTitle = (ev as unknown as { title?: string }).title;
    if (fromTitle && fromTitle.trim()) return fromTitle.trim();
    const cleaned = (ev.text || '').replace(/\s+/g, ' ').trim();
    if (cleaned) {
      return cleaned.length > 80 ? `${cleaned.slice(0, 80)}…` : cleaned;
    }
    return 'תיעוד מהמערכת';
  };

  const handleOpenShareDialog = async () => {
    if (!event || isSharing) return;
    setIsSharing(true);
    try {
      const { shareUrl } = await EventService.generateShareLink(event.id);
      if (!shareUrl) {
        toast({
          title: 'לא ניתן ליצור קישור שיתוף',
          variant: 'destructive',
        });
        return;
      }
      setShareUrl(shareUrl);
      setShareDialogOpen(true);
    } catch (e) {
      toast({
        title: 'לא ניתן ליצור קישור שיתוף',
        variant: 'destructive',
      });
    } finally {
      setIsSharing(false);
    }
  };

  const shareViaWhatsApp = async (url: string) => {
    if (!event) return;
    const title = resolveShareTitle(event);
    const message = `שלום, מצורף תיעוד מהמערכת:\n${title}\n${url}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    const opened = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    if (!opened) {
      try {
        await navigator.clipboard.writeText(url);
        toast({
          title: 'הקישור הועתק',
          description: 'לא ניתן היה לפתוח את וואטסאפ. הקישור הועתק ללוח.',
        });
      } catch {
        toast({
          title: 'לא ניתן ליצור קישור שיתוף',
          variant: 'destructive',
        });
      }
    }
    setShareDialogOpen(false);
  };

  const shareViaGmail = (url: string) => {
    if (!event) return;
    const subject = encodeURIComponent('קישור לתיעוד');
    const body = encodeURIComponent(
      `שלום, מצורף תיעוד מהמערכת:\n${resolveShareTitle(event)}\n${url}`
    );
    window.open(
      `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`,
      '_blank',
      'noopener,noreferrer'
    );
    setShareDialogOpen(false);
  };

  const triggerDownload = async (file: ProjectFile) => {
    if (file.url === '#mock-url') {
      toast({
        title: 'הורדה לא זמינה',
        description: 'קובץ זה הוא קובץ לדוגמה ואינו זמין להורדה',
        variant: 'destructive'
      });
      return;
    }

    const result = await downloadMedia({
      url: file.url,
      fileName: file.name || 'download',
      // Reuse DocumentsTab's exact mimeType strategy: only VIDEO is special-cased;
      // the IMAGE enum is intentionally NOT mapped to image/*.
      mimeType: file.type === 'VIDEO' ? 'video/mp4' : file.type || 'application/octet-stream',
    });

    if (!result.success) {
      toast({ title: 'ההורדה נכשלה', variant: 'destructive' });
      return;
    }

    // Native saves to the public Downloads folder and confirms it; web stays silent.
    if (result.platform === 'native') {
      toast({ title: 'הקובץ נשמר בתיקיית ההורדות' });
    }
  };

  return (
    <>
      <Dialog  open={open && !isGalleryOpen && !videoModalIsOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="max-w-lg w-[95vw]" dir="rtl"
        onInteractOutside={(e) => {
            if (isGalleryOpen) {
              e.preventDefault(); // מונע מהמודאל הראשי להיסגר (Prevents the main modal from closing)
              return
            }
          }}>
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                eventTypeColors[event.type]
              )}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-right">
                  {eventTypeLabels[event.type]}
                </DialogTitle>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(event.date), 'd בMMMM yyyy', { locale: he })}
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <p className="text-sm leading-relaxed">{event.text}</p>
            </div>

            {event.type === 'progress' && event.answer?.trim() && (
              <div className="bg-accent rounded-xl p-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="font-medium text-sm text-accent-foreground">תשובה</h4>
                  {event.answeredAt && !Number.isNaN(new Date(event.answeredAt).getTime()) && (
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(event.answeredAt), 'd בMMMM yyyy HH:mm', { locale: he })}
                    </span>
                  )}
                </div>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{event.answer}</p>
              </div>
            )}

            {event.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {event.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-3 py-1 bg-accent text-accent-foreground rounded-full text-sm"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            )}

            {attachedFiles.length > 0 && (
              <div>
                <h4 className="font-medium mb-3 text-sm">קבצים מצורפים ({attachedFiles.length})</h4>
                <div className="grid grid-cols-3 gap-2">
                  {attachedFiles.map((file) => (
                    <div
                      key={file.id}
                      onClick={() => handleFileClick(file)}
                      className="aspect-square bg-secondary rounded-lg overflow-hidden relative group cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                    >
                      {file.type === 'IMAGE' || file.type === 'VIDEO' ? (
                        <img
                          src={file.thumbnailUrl}
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                      ) : file.type.startsWith('image/') ? (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-muted-foreground" />
                        </div>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center p-2">
                          <FileText className="w-6 h-6 text-muted-foreground mb-1" />
                          <span className="text-xs text-muted-foreground text-center truncate w-full">
                            {file.name}
                          </span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <p className="text-white text-xs text-center px-2 truncate">{file.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={handleOpenShareDialog}
              disabled={isSharing}
            >
              {isSharing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin ml-2" />
                  משתף...
                </>
              ) : (
                'שתף קישור'
              )}
            </Button>
            {onEdit && event && (
              <Button variant="outline" onClick={() => onEdit(event)}>
                ערוך
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>
              סגור
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="max-w-sm" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">בחר אופן שיתוף</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            <Button onClick={() => shareUrl && shareViaGmail(shareUrl)}>
              שליחה ב-Gmail
            </Button>
            <Button onClick={() => shareUrl && shareViaWhatsApp(shareUrl)}>
              שליחה בוואטסאפ
            </Button>
            <Button variant="outline" onClick={() => setShareDialogOpen(false)}>
              ביטול
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewFile && !isImageMedia({ type: previewFile.type })} onOpenChange={(isOpen) => !isOpen && setPreviewFile(null)}>
        <DialogContent className="max-w-4xl w-[95vw]" dir="rtl"
          onInteractOutside={(e) => {
            
            if (isGalleryOpen) {
              e.preventDefault(); // מונע מהמודאל הראשי להיסגר (Prevents the main modal from closing)
            }
          }}
        >
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="truncate flex-1 text-right">
              {previewFile?.name}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => previewFile && triggerDownload(previewFile)}
              >
                <Download className="w-4 h-4 ml-2" />
                הורד
              </Button>
            </div>
          </DialogHeader>
          {isPdfMedia({ type: previewFile?.type }) && (
            previewFile.url === '#mock-url' ? (
              <div className="flex items-center justify-center h-[80vh] text-muted-foreground">
                <div className="text-center">
                  <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>תצוגה מקדימה לא זמינה לקובץ לדוגמה</p>
                </div>
              </div>
            ) : (
              <iframe
                src={previewFile.url}
                className="w-full h-[80vh] rounded-lg"
                title={previewFile.name}
              />
            )
          )}
        </DialogContent>
      </Dialog>

      {isGalleryOpen && (
        <ImageGallery
          images={imageFiles}
          initialIndex={galleryIndex}
          isOpen={isGalleryOpen}
          onClose={handleGalleryClosed}
          enableDownload={true}
        />
      )}

      {videoModalIsOpen && (
        <VideoModal
          isOpen={videoModalIsOpen}
          onClose={() => setVideoModalIsOpen(false)}
          videoUrl={previewVideo?.url || ''}
        />
      )}
    </>
  );
}